import { useState } from "react";

import type { ToolStepState } from "./agentReducer";

/** French labels; the whole surface is French by default. */
const TOOL_LABELS: Record<string, string> = {
  search_documents: "Recherche dans les documents",
  verifier_profession_cnp: "Vérification de la profession (CNP 2021)",
  recherche_web: "Recherche web",
  generer_description_de_poste: "Génération d'une description de poste",
  generer_echelle_salariale: "Génération d'une échelle salariale",
  rediger_document: "Rédaction d'un document",
};

const STATUS_STYLES: Record<string, string> = {
  running: "bg-brand-surface text-brand-muted",
  ok: "bg-brand-teal/10 text-brand-primary",
  empty: "bg-brand-surface text-brand-muted",
  insufficient_data: "bg-brand-accent/10 text-brand-accent-dk",
  error: "bg-brand-error/10 text-brand-error",
};

function primaryArgument(args: Record<string, unknown>): string | null {
  const value = args.query ?? args.titre ?? args.poste ?? args.demande;
  return typeof value === "string" && value.trim() ? value : null;
}

export interface ToolStepProps {
  step: ToolStepState;
}

/**
 * One tool invocation, shown while the turn runs and kept afterwards.
 *
 * Deliberately quiet: a single muted line per call, so the steps read as a
 * footnote above the answer rather than a card competing with it. The result
 * summary sits in the tooltip and the raw arguments open on click.
 */
export function ToolStep({ step }: ToolStepProps) {
  const [open, setOpen] = useState(false);
  const label = TOOL_LABELS[step.name] ?? step.name;
  const arg = primaryArgument(step.arguments);
  const running = step.status === "running";

  const detail = running
    ? "en cours"
    : [step.summary, step.durationMs != null ? `${step.durationMs} ms` : null]
        .filter(Boolean)
        .join(" · ");

  return (
    <div className="text-xs text-brand-muted">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 py-1 text-left hover:text-brand-text"
        aria-expanded={open}
        title={detail}
      >
        <span
          className={`inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[10px] ${
            STATUS_STYLES[step.status] ?? STATUS_STYLES.ok
          }`}
        >
          {running ? <span className="animate-pulse">·</span> : "✓"}
        </span>
        <span className="min-w-0 truncate">
          <span className={running ? "animate-pulse" : ""}>{label}</span>
          {arg && <span className="opacity-70"> « {arg} »</span>}
        </span>
      </button>

      {open && (
        <div className="mb-1 ml-6 rounded-md bg-brand-bg/60 px-3 py-2 text-[11px]">
          <p className="mb-1">{detail}</p>
          <pre className="overflow-x-auto whitespace-pre-wrap">{JSON.stringify(step.arguments, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
